import { Sparkles, ToggleRight, Database, Bot, Search, Users, Layers } from "lucide-react";
import { IMAGES, PIPELINE_STAGES, TONES } from "@/data/content";
import { Fragment, MiniBars, Sparkline } from "@/components/Decor";

const Card = ({ children, className = "", style }) => (
  <div className={`rounded-2xl border border-[#0E1B31]/[0.07] bg-white shadow-[0_20px_50px_-35px_rgba(14,27,49,0.4)] ${className}`} style={style}>
    {children}
  </div>
);

const Strategy = ({ s }) => {
  const t = TONES.copper;
  return (
    <div className="relative h-[420px]" data-testid="visual-strategy">
      <img src={IMAGES.planning} alt="" className="absolute right-0 top-0 h-[70%] w-[78%] rounded-[30px] object-cover" loading="lazy" />
      <div className="absolute right-0 top-0 h-[70%] w-[78%] rounded-[30px]" style={{ background: `linear-gradient(135deg, ${t.accent}55, transparent 60%)` }} />
      {s.receives.map((r, i) => (
        <Card key={r} className={`absolute left-0 w-[64%] p-5 ${i === 1 ? "float-slow" : ""}`} style={{ top: `${38 + i * 19}%`, marginLeft: i * 18, zIndex: 3 - i }}>
          <p className="font-mono-brand text-[9px] tracking-[0.25em] uppercase text-[#6B7A8F]">Παράδοση 0{i + 1}</p>
          <p className="mt-1.5 text-sm font-medium text-[#0E1B31] leading-snug">{r}</p>
          <span className="mt-3 block h-1 w-16 rounded-full" style={{ background: `linear-gradient(90deg, ${t.accent}, ${t.soft})` }} />
        </Card>
      ))}
    </div>
  );
};

const Exports = () => {
  const t = TONES.slate;
  const dots = [
    [22, 42],
    [48, 30],
    [60, 52],
    [78, 38],
    [84, 62],
  ];
  return (
    <div className="relative h-[420px] overflow-hidden rounded-[30px] border border-[#0E1B31]/[0.07] bg-white" data-testid="visual-exports">
      <img src={IMAGES.map} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30 saturate-50" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        <path d={`M ${dots.map((d) => d.join(" ")).join(" L ")}`} fill="none" stroke={t.accent} strokeWidth="0.5" strokeDasharray="1.2 1.2" className="draw-line" vectorEffect="non-scaling-stroke" />
      </svg>
      {dots.map(([x, y], i) => (
        <span key={i} className={`absolute -translate-x-1/2 -translate-y-1/2 flex h-4 w-4 items-center justify-center rounded-full ${i === 0 ? "pulse-ring" : ""}`} style={{ left: `${x}%`, top: `${y}%`, color: t.accent }}>
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: i === 0 ? TONES.copper.accent : t.accent }} />
        </span>
      ))}
      <Fragment className="absolute left-5 top-5 w-[210px]">
        <p className="font-mono-brand text-[9px] tracking-[0.25em] uppercase text-[#6B7A8F]">Ιεράρχηση Αγορών</p>
        <ul className="mt-2 space-y-1.5">
          {["Ελκυστικότητα αγοράς", "Ανταγωνισμός & τιμές", "Κανάλια & distributors"].map((l, i) => (
            <li key={l} className="flex items-center gap-2 text-[11px] text-[#3B4A5E]">
              <span className="h-1.5 w-8 rounded-full" style={{ background: `${t.accent}${["FF", "AA", "66"][i]}` }} /> {l}
            </li>
          ))}
        </ul>
      </Fragment>
      <div className="absolute inset-x-5 bottom-5 flex flex-wrap gap-2">
        {["Έρευνα", "Επιλογή χώρας", "Buyers & Distributors", "Πρώτη συμφωνία", "Ανάπτυξη δικτύου"].map((st, i) => (
          <span key={st} className="rounded-full border bg-white/90 px-3 py-1.5 text-[11px] font-medium text-[#0E1B31]" style={{ borderColor: `${t.accent}44` }}>
            <span className="font-mono-brand text-[9px] mr-1.5" style={{ color: t.accent }}>0{i + 1}</span>
            {st}
          </span>
        ))}
      </div>
    </div>
  );
};

const Transformation = () => {
  const t = TONES.sage;
  return (
    <div className="relative h-[420px] rounded-[30px] overflow-hidden border border-[#0E1B31]/[0.07]" data-testid="visual-transformation" style={{ background: `linear-gradient(160deg, #fff, ${t.tint})` }}>
      <img src={IMAGES.production} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.10]" loading="lazy" />
      <div className="relative grid h-full grid-cols-3 gap-4 p-6">
        {[
          { label: "Σήμερα", desc: "Ασύνδετες εργασίες, εξάρτηση από πρόσωπα", icon: Layers, chaos: true },
          { label: "Ανασχεδιασμός", desc: "Ροές, ρόλοι, σημεία ελέγχου", icon: Search },
          { label: "Σύστημα", desc: "Επαναλήψιμη λειτουργία με δείκτες", icon: Database },
        ].map((c, i) => (
          <div key={c.label} className="flex flex-col">
            <Card className="flex-1 p-4 flex flex-col">
              <p className="flex items-center gap-2 font-mono-brand text-[9px] tracking-[0.25em] uppercase text-[#6B7A8F]">
                <c.icon className="h-3.5 w-3.5" style={{ color: t.accent }} /> {c.label}
              </p>
              <div className="mt-4 flex-1 relative">
                {c.chaos ? (
                  [0, 1, 2, 3, 4].map((k) => (
                    <span key={k} className="absolute h-7 w-14 rounded-lg border border-[#0E1B31]/15 bg-[#F2EEE7]" style={{ left: `${(k * 37) % 55}%`, top: `${(k * 53) % 70}%`, transform: `rotate(${k * 13 - 20}deg)` }} />
                  ))
                ) : i === 1 ? (
                  <div className="space-y-2">
                    {[0, 1, 2, 3].map((k) => (
                      <div key={k} className="flex items-center gap-2">
                        <span className="h-6 w-6 rounded-md" style={{ background: `${t.accent}${["FF", "CC", "99", "66"][k]}` }} />
                        <span className="h-2 flex-1 rounded-full bg-[#0E1B31]/10" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {[0, 1, 2, 3].map((k) => (
                      <span key={k} className="h-9 rounded-lg border" style={{ borderColor: `${t.accent}66`, background: k % 2 ? t.tint : "#fff" }} />
                    ))}
                    <MiniBars color={t.accent} className="col-span-2 !h-8" heights={[50, 65, 80, 95]} />
                  </div>
                )}
              </div>
              <p className="mt-4 text-[11px] leading-snug text-[#3B4A5E]">{c.desc}</p>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

const AI = () => (
  <div className="relative h-[420px] rounded-[30px] overflow-hidden mesh-navy border border-white/10" data-testid="visual-ai">
    <div className="perspective-grid light" />
    <div className="glass-dark absolute left-6 top-6 w-[62%] rounded-2xl p-5 text-white">
      <p className="flex items-center gap-2 font-mono-brand text-[9px] tracking-[0.25em] uppercase text-white/60">
        <Bot className="h-3.5 w-3.5 text-[#8B9EB7]" /> Workflow με υποστήριξη AI
      </p>
      <div className="mt-4 space-y-3">
        {["Συλλογή αιτημάτων πελατών", "Κατηγοριοποίηση & προτεραιότητα", "Προσχέδιο απάντησης", "Έλεγχος από την ομάδα"].map((st, i) => (
          <div key={st} className="flex items-center gap-3">
            <span className="flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-mono-brand" style={{ background: i === 3 ? "#C5A059" : "rgba(255,255,255,0.12)" }}>{i + 1}</span>
            <span className="text-[12px] text-white/85">{st}</span>
            {i < 3 && <ToggleRight className="ml-auto h-4 w-4 text-[#5A7265]" />}
          </div>
        ))}
      </div>
    </div>
    <div className="glass-dark absolute right-6 top-[38%] w-[40%] rounded-2xl p-4 text-white float-mid">
      <p className="flex items-center gap-2 font-mono-brand text-[9px] tracking-[0.25em] uppercase text-white/60"><Sparkles className="h-3 w-3 text-[#C5A059]" /> Πληροφόρηση</p>
      <Sparkline color="#8B9EB7" className="mt-2" />
      <p className="mt-1 text-[11px] text-white/70">Δεδομένα → εικόνα → απόφαση</p>
    </div>
    <div className="glass-dark absolute left-10 bottom-6 w-[52%] rounded-2xl p-4 text-white float-slow">
      <p className="font-mono-brand text-[9px] tracking-[0.25em] uppercase text-white/60">Χρόνος που επιστρέφει στην ομάδα</p>
      <div className="mt-2 flex gap-1">
        {[...Array(14)].map((_, i) => (
          <span key={i} className="h-5 flex-1 rounded-sm" style={{ background: i < 9 ? "#5A7265" : "rgba(255,255,255,0.12)" }} />
        ))}
      </div>
      <p className="mt-2 text-[11px] text-white/70">Επαναλαμβανόμενες εργασίες → αυτοματοποίηση</p>
    </div>
  </div>
);

const BI = () => {
  const t = TONES.bluegrey;
  return (
    <div className="relative h-[420px] grid grid-cols-6 grid-rows-6 gap-3" data-testid="visual-bi">
      <Card className="col-span-4 row-span-4 p-5 flex flex-col">
        <p className="font-mono-brand text-[9px] tracking-[0.25em] uppercase text-[#6B7A8F]">Dashboard Απόδοσης</p>
        <div className="mt-4 flex-1 flex items-end gap-2">
          {[35, 52, 44, 68, 60, 84, 72, 96, 88, 78, 92, 100].map((h, i) => (
            <span key={i} className="bar-grow flex-1 rounded-t-md" style={{ height: `${h}%`, background: i % 4 === 3 ? TONES.copper.accent : `${t.accent}${i % 2 ? "FF" : "88"}`, animationDelay: `${i * 60}ms` }} />
          ))}
        </div>
        <div className="mt-3 flex gap-4 text-[10px] text-[#6B7A8F]">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: t.accent }} /> Πωλήσεις ανά αγορά</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#C26D52]" /> Νέοι πελάτες</span>
        </div>
      </Card>
      <Card className="col-span-2 row-span-2 p-4 flex flex-col justify-between">
        <p className="font-mono-brand text-[9px] tracking-[0.25em] uppercase text-[#6B7A8F]">Τάση αγοράς</p>
        <Sparkline color={TONES.sage.accent} />
      </Card>
      <Card className="col-span-2 row-span-4 p-4">
        <p className="flex items-center gap-2 font-mono-brand text-[9px] tracking-[0.25em] uppercase text-[#6B7A8F]"><Users className="h-3 w-3" style={{ color: t.accent }} /> Ανταγωνισμός</p>
        <ul className="mt-3 space-y-2.5">
          {["Τιμή", "Κανάλια", "Positioning", "Προϊόν"].map((l, i) => (
            <li key={l}>
              <p className="text-[10px] text-[#3B4A5E]">{l}</p>
              <span className="mt-1 block h-1.5 rounded-full bg-[#0E1B31]/8">
                <span className="block h-full rounded-full" style={{ width: `${[72, 55, 88, 40][i]}%`, background: t.accent }} />
              </span>
            </li>
          ))}
        </ul>
      </Card>
      <Card className="col-span-4 row-span-2 p-4 flex items-center gap-4" style={{ background: `linear-gradient(90deg, #fff, ${t.tint})` }}>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0E1B31] text-white"><Search className="h-4 w-4" /></span>
        <div>
          <p className="text-[12px] font-semibold text-[#0E1B31]">Έρευνα → Συμπέρασμα → Απόφαση</p>
          <p className="text-[11px] text-[#3B4A5E]">Κάθε report καταλήγει σε συγκεκριμένη πρόταση ενέργειας.</p>
        </div>
      </Card>
    </div>
  );
};

const Commercial = () => {
  const t = TONES.gold;
  return (
    <div className="relative h-[420px] rounded-[30px] overflow-hidden border border-[#0E1B31]/[0.07]" data-testid="visual-commercial" style={{ background: `linear-gradient(160deg, #fff, ${t.tint})` }}>
      <img src={IMAGES.commercial} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" loading="lazy" />
      <div className="relative h-full flex flex-col p-6">
        <p className="font-mono-brand text-[9px] tracking-[0.25em] uppercase text-[#6B7A8F]">Εμπορικός κύκλος · Επαναλήψιμη ροή</p>
        <div className="mt-6 flex-1 grid grid-cols-6 gap-2 items-end">
          {PIPELINE_STAGES.map((st, i) => (
            <div key={st} className="flex flex-col items-center gap-2 h-full justify-end">
              <Card className="w-full p-2 flex flex-col items-center gap-1.5" style={{ height: `${100 - i * 11}%`, background: `linear-gradient(180deg, #fff, ${i < 3 ? TONES.copper.tint : t.tint})` }}>
                <span className="flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-mono-brand text-white" style={{ background: i < 3 ? TONES.copper.accent : t.accent }}>{i + 1}</span>
                <div className="flex-1 w-full flex flex-col gap-1 justify-end">
                  {[...Array(6 - i)].map((_, k) => (
                    <span key={k} className="h-1.5 w-full rounded-full" style={{ background: `${i < 3 ? TONES.copper.accent : t.accent}${k === 0 ? "FF" : "44"}` }} />
                  ))}
                </div>
              </Card>
              <span className="text-[9px] sm:text-[10px] font-medium text-[#3B4A5E] text-center leading-tight">{st}</span>
            </div>
          ))}
        </div>
        <div className="mt-5 flex items-center gap-3 rounded-2xl bg-[#0E1B31] px-4 py-3 text-white">
          <span className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-[#C5A059]">CRM</span>
          <span className="h-4 w-px bg-white/20" />
          <p className="text-[11px] text-white/80">Υπεύθυνος, επόμενο βήμα και ημερομηνία σε κάθε ευκαιρία.</p>
        </div>
      </div>
    </div>
  );
};

export const ServiceVisual = ({ service }) => {
  switch (service.id) {
    case "strategy":
      return <Strategy s={service} />;
    case "exports":
      return <Exports />;
    case "transformation":
      return <Transformation />;
    case "ai-digital":
      return <AI />;
    case "bi-market":
      return <BI />;
    default:
      return <Commercial />;
  }
};
